import React, { useState, useEffect } from 'react';
import LoadingSpinner from './LoadingSpinner';

const PageLoader = ({ 
  children, 
  isLoading, 
  error, 
  onRetry,
  loadingMessage = 'Loading page...',
  errorMessage = 'Something went wrong',
  showRetryButton = true,
  className = ''
}) => {
  const [isNavigating, setIsNavigating] = useState(false);

  useEffect(() => {
    // Detect page navigation/refresh
    const handleBeforeUnload = () => {
      setIsNavigating(true);
    };

    const handleLoad = () => {
      setIsNavigating(false);
    };

    // Listen for navigation events
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('load', handleLoad);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('load', handleLoad);
    };
  }, []);

  // Show loading state
  if (isLoading || isNavigating) {
    return (
      <div className={`min-h-screen bg-gray-50 ${className}`}>
        <div className="p-6">
          <LoadingSpinner 
            size="large" 
            message={isNavigating ? 'Navigating...' : loadingMessage}
            className="min-h-[400px]"
          />
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className={`min-h-screen bg-gray-50 ${className}`}>
        <div className="p-6">
          <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Page</h2>
            <p className="text-gray-600 mb-6">{errorMessage}</p>
            {showRetryButton && onRetry && (
              <button
                onClick={onRetry}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Render children when everything is loaded
  return children;
};

export default PageLoader;
